import React, {useState, useEffect} from "react";
import {useTranslation} from "react-i18next";
import {Helmet} from "react-helmet";
import {useSearchParams, Link} from "react-router-dom";
import "../../assets/stylesheets/blog.css";
import facebookIcon from "../../assets/image/facebook.svg";
import InstaIcon from "../../assets/image/instagram.svg";
import TelegramIcon from "../../assets/image/telegram.svg";
import axios from "axios";
import DOMPurify from "dompurify";

function Blog() {
    const {t, i18n} = useTranslation(["blog"]);
    const [post, setPost] = useState({});
    const [allPosts, setAllPosts] = useState([]);
    const [searchParams, setSearchParams] = useSearchParams();
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    const blogUrl = window.location.href;

    const shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
        `https://sievn.am/${i18n.language}/blog?id=${
            post?.blogId
        }`
    )}&quote=${encodeURIComponent(post?.content)}`;

    const fetchPosts = async (lng) => {
        try {
            const res = await axios.get(
                `${process.env.REACT_APP_MY_API_KAY}/blog/getAll?language=${lng}`
            );
            setAllPosts(res.data);

            if (!searchParams.get("id") && res.data.length > 0) {
                searchParams.set("id", res.data[res.data.length - 1]?.blogId);
                setSearchParams(searchParams, {replace: true});
            }
        } catch (error) {
            console.error("Error fetching posts:", error);
        }
    };

    useEffect(() => {
        fetchPosts(i18n.language);
    }, [i18n.language]);

    const fetchPost = async (lng) => {
        try {
            const res = await axios.get(
                `${process.env.REACT_APP_MY_API_KAY}/blog?blogId=${searchParams.get(
                    "id"
                )}&language=${lng}`
            );
            setPost(res.data);
            // Set initial carousel index to the main image, if available
            if (res.data.images && res.data.images.length > 0) {
                const mainIndex = res.data.images.findIndex((img) => img.imageOrder===1) || 0;
                setCurrentImageIndex(mainIndex !== -1 ? mainIndex : 0);
            } else {
                setCurrentImageIndex(0);
            }
        } catch (error) {
            console.error("Error fetching post:", error);
        }
    };

    useEffect(() => {
        if (!searchParams.get("id")) return;
        fetchPost(i18n.language);
    }, [i18n.language, searchParams.toString()]);

    const extractTextFromHTML = (html) => {
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = html;
        return tempDiv.innerText || tempDiv.textContent || "";
    };

    const sanitizedTitle = DOMPurify.sanitize(post?.title);
    const sanitizedContent = DOMPurify.sanitize(post?.content);
    const titleText = extractTextFromHTML(sanitizedTitle);
    const contentText = extractTextFromHTML(sanitizedContent);

    const shareOnInstagram = () => {
        const blogUrl = encodeURIComponent(
            `https://sievn.am/${i18n.language}/blog?id=${
                post?.blogId
            }`
        );
        const caption = encodeURIComponent("Check out this blog post!");
        if (/iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) {
            window.location.href = `instagram://library?AssetPath=${blogUrl}&caption=${caption}`;
        } else {
            window.open("https://www.instagram.com/", "_blank");
        }
    };

    const images = post?.images?.length > 0 ? post.images : [
        {url: "https://thumbs.dreamstime.com/b/residential-real-estate-buying-selling-renting-homes-apartments-condos-townhouses-investment-shelter-expert-guidance-318813833.jpg"},
        {url: "https://t4.ftcdn.net/jpg/03/00/10/35/360_F_300103505_oBLUa4dEG8mFdP60givbyNdoy7aHFmuu.jpg"}];

    const handlePrevImage = () => {
        setCurrentImageIndex((prev) =>
            prev === 0 ? images.length - 1 : prev - 1
        );
    };

    const handleNextImage = () => {
        setCurrentImageIndex((prev) =>
            prev === images.length - 1 ? 0 : prev + 1
        );
    };

    return (
        <>
            <Helmet>
                <link
                    rel="canonical"
                    href={`https://sievn.am/${i18n.language}/blog?id=${
                        post?.blogId
                    }`}
                />
                <meta property="og:title" content={titleText}/>
                <meta property="og:site_name" content="sievn.am"/>
                <meta
                    property="og:url"
                    content={`https://sievn.am/${i18n.language}/blog?id=${
                        post?.blogId
                    }`}
                />
                <meta property="og:description" content={contentText}/>
                <meta property="og:type" content="website"/>
                <meta
                    property="og:image"
                    content={images[0]?.url}
                />
                <meta property="og:image:width" content="1200"/>
                <meta property="og:image:height" content="630"/>
                <meta name="description" content={t("blogDescribe")}/>
                <title>{t("blogTitle")}</title>
            </Helmet>
            <div className="blog-page">
                <div className="main-container-blog">
                    <div className="headings-blog">
                        <h1>
                            <em>{t("blog")}</em>
                        </h1>
                    </div>
                    <div className="blog-section-blog">
                        <div className="blog-nav-blog">
                            {allPosts
                                .map((item) => (
                                    <div className="blog-link-blog" key={item?.blogId}>
                                        <Link
                                            to={`?id=${item?.blogId}`}
                                            dangerouslySetInnerHTML={{
                                                __html: DOMPurify.sanitize(item?.title),
                                            }}
                                            onClick={() => setPost(item)}
                                        />
                                    </div>
                                ))
                                .reverse()}
                        </div>
                        <div className="content-blog">
                            {post?.blogId && (
                                <>
                                    <div className="carousel-blog">
                                        {images.map((image, index) => (
                                            <img
                                                key={index}
                                                className={`carousel-image-blog ${
                                                    index === currentImageIndex ? "active" : ""
                                                }`}
                                                src={image.url}
                                                alt={titleText || "Blog post"}
                                            />
                                        ))}
                                        {images.length > 1 && (
                                            <>
                                                <button
                                                    className="carousel-button-blog prev"
                                                    onClick={handlePrevImage}
                                                >
                                                    ‹
                                                </button>
                                                <button
                                                    className="carousel-button-blog next"
                                                    onClick={handleNextImage}
                                                >
                                                    ›
                                                </button>
                                            </>
                                        )}
                                    </div>
                                    <h3
                                        className="title-blog"
                                        dangerouslySetInnerHTML={{
                                            __html: DOMPurify.sanitize(post?.title),
                                        }}
                                    />
                                    <p
                                        className="text-blog"
                                        dangerouslySetInnerHTML={{
                                            __html: DOMPurify.sanitize(post?.content),
                                        }}
                                    />
                                    <div className="social-block-blog">
                                        <p className="meta-blog">
                                            <span>{post?.publishDate}</span>
                                            <span
                                                dangerouslySetInnerHTML={{
                                                    __html: DOMPurify.sanitize(post?.author),
                                                }}
                                            />
                                        </p>
                                        <div className="social-icons-blog">
                                            <Link
                                                to="#"
                                                onClick={() =>
                                                    window.open(shareUrl, "_blank", "width=600,height=400")
                                                }
                                                rel="noopener noreferrer"
                                            >
                                                <img src={facebookIcon} alt="Facebook"/>
                                            </Link>
                                            <Link to="#" onClick={shareOnInstagram}>
                                                <img src={InstaIcon} alt="Instagram"/>
                                            </Link>
                                            <Link
                                                to="#"
                                                onClick={() =>
                                                    window.open(
                                                        `https://telegram.me/share/url?url=${encodeURIComponent(
                                                            blogUrl
                                                        )}&text=${encodeURIComponent(titleText)}`,
                                                        "_blank",
                                                        "width=600,height=400"
                                                    )
                                                }
                                            >
                                                <img src={TelegramIcon} alt="Telegram"/>
                                            </Link>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default Blog;